/**
 * SDK Demo Page — "Verify Yourself"
 * ===================================
 * Overhauled onboarding experience. Two modes:
 *
 * - Sandbox: Mock results, instant, free (test API key)
 * - Production: Real AI verification with actual risk scoring (live API key)
 *
 * Pre-fills with logged-in user's real data. Auto-selects tenant API key.
 * Post-completion redirects to the applicant profile.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Play,
  ArrowLeft,
  Shield,
  Loader2,
  AlertCircle,
  CheckCircle,
  FlaskConical,
  Rocket,
} from 'lucide-react';
import VerificationSDK from './VerificationSDK';
import { BRAND } from '../../config/brand';

// API base URL for backend
const API_BASE = process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, '').replace(/\/$/, '')
  || 'https://getclearance-production.up.railway.app';

export default function SDKDemoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Core state
  const [mode, setMode] = useState(null); // 'sandbox' | 'production'
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSDK, setShowSDK] = useState(false);
  const [result, setResult] = useState(null);

  // Manual key entry fallback (for unauthenticated or external users)
  const [manualKeyMode, setManualKeyMode] = useState(false);
  const [manualKey, setManualKey] = useState('');

  // If apiKey from URL, use manual mode
  useEffect(() => {
    const urlApiKey = searchParams.get('apiKey');
    if (urlApiKey) {
      setManualKeyMode(true);
      setManualKey(urlApiKey);
    }
  }, [searchParams]);

  // Get user's display data for pre-filling
  // Parse from email prefix when Auth0 profile lacks given_name (chris.diyanni@ → Chris / Diyanni)
  const emailParts = user?.email?.split('@')[0]?.split(/[._-]/) || [];
  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
  const firstName = user?.given_name
    || (user?.name && !user.name.includes('@') ? user.name.split(' ')[0] : '')
    || capitalize(emailParts[0]) || '';
  const lastName = user?.family_name
    || (user?.name && !user.name.includes('@') ? user.name.split(' ').slice(1).join(' ') : '')
    || capitalize(emailParts[1]) || '';
  const userEmail = user?.email || '';

  // Start demo with selected mode
  const startDemo = useCallback(async (selectedMode) => {
    const modeToUse = selectedMode || mode;
    if (!modeToUse && !manualKeyMode) return;

    setLoading(true);
    setError(null);

    try {
      // For manual key mode, use the API key endpoint directly
      if (manualKeyMode) {
        const response = await fetch(`${API_BASE}/api/v1/sdk/access-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': manualKey,
          },
          body: JSON.stringify({
            external_user_id: `demo_user_${Date.now()}`,
            email: userEmail || 'demo@example.com',
            first_name: firstName || 'Demo',
            last_name: lastName || 'User',
            flow_name: 'default',
            redirect_url: window.location.origin + '/sdk/demo',
            expires_in: 3600,
          }),
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.detail || `API Error: ${response.status}`);
        }

        const data = await response.json();
        setAccessToken(data.access_token);
        setShowSDK(true);
        setResult(null);
        return;
      }

      // For authenticated users, use Bearer token with demo endpoint
      if (!isAuthenticated) {
        setError('Please sign in to use the demo.');
        setLoading(false);
        return;
      }

      const bearerToken = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE}/api/v1/sdk/demo-access-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${bearerToken}`,
        },
        body: JSON.stringify({
          external_user_id: `onboarding_${user.sub}`,
          email: userEmail || 'demo@example.com',
          first_name: firstName || 'Demo',
          last_name: lastName || 'User',
          flow_name: 'default',
          redirect_url: window.location.origin + '/sdk/demo',
          expires_in: 3600,
          sandbox: modeToUse === 'sandbox',
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `API Error: ${response.status}`);
      }

      const data = await response.json();
      setAccessToken(data.access_token);
      setShowSDK(true);
      setResult(null);
    } catch (err) {
      setError(err.message);
      setShowSDK(false);
    } finally {
      setLoading(false);
    }
  }, [mode, manualKeyMode, manualKey, user, isAuthenticated, getAccessTokenSilently, userEmail, firstName, lastName]);

  const handleComplete = (completionResult) => {
    setResult(completionResult);
    setShowSDK(false);

    // Mark onboarding step as complete
    localStorage.setItem('gc_onboarding_tested', 'true');

    // Redirect to applicant profile after delay
    const applicantId = completionResult?.applicant_id;
    if (applicantId) {
      const testModeParam = mode === 'sandbox' ? '?test_mode=true' : '';
      setTimeout(() => {
        navigate(`/applicants/${applicantId}${testModeParam}`);
      }, 1500);
    }
  };

  const handleError = (err) => {
    setError(err.message || 'Verification failed');
    setShowSDK(false);
  };

  const handleBack = () => {
    setShowSDK(false);
    setAccessToken('');
    setResult(null);
  };

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    setError(null);
    startDemo(selectedMode);
  };

  // SDK fullscreen view
  if (showSDK) {
    return (
      <div className="sdk-demo-page">
        <div className="demo-container" style={{ maxWidth: '600px' }}>
          <div className="sdk-fullscreen">
            <button className="back-btn" onClick={handleBack}>
              <ArrowLeft size={18} />
              Exit Demo
            </button>
            <VerificationSDK
              accessToken={accessToken}
              baseUrl={`${API_BASE}/api/v1/sdk`}
              onComplete={handleComplete}
              onError={handleError}
            />
          </div>
        </div>
        <style>{sdkDemoStyles}</style>
      </div>
    );
  }

  // Success result with redirect
  if (result) {
    return (
      <div className="sdk-demo-page">
        <div className="demo-container">
          <div className="result-state">
            <div className="result-icon success">
              <CheckCircle size={48} />
            </div>
            <h2>Verification Complete!</h2>
            <p>
              {result.applicant_id
                ? "Redirecting to your applicant profile..."
                : "The applicant data has been captured and is being processed."}
            </p>
            <div className="result-actions">
              {result.applicant_id ? (
                <button
                  className="btn-primary"
                  onClick={() => navigate(`/applicants/${result.applicant_id}${mode === 'sandbox' ? '?test_mode=true' : ''}`)}
                >
                  <CheckCircle size={18} />
                  View My Profile Now
                </button>
              ) : (
                <>
                  <button className="btn-primary" onClick={() => { setMode(null); setResult(null); }}>
                    <Play size={18} />
                    Run Again
                  </button>
                  <a href="/applicants" className="btn-secondary">
                    View in Dashboard
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
        <style>{sdkDemoStyles}</style>
      </div>
    );
  }

  // Manual key entry (fallback for URL-based or unauthenticated)
  if (manualKeyMode) {
    return (
      <div className="sdk-demo-page">
        <div className="demo-container">
          <div className="demo-header">
            <div className="logo">
              <Shield size={32} />
              <span>{BRAND.name}</span>
            </div>
            <h1>SDK Demo</h1>
            <p>Test the verification flow your customers will experience</p>
          </div>

          <div className="start-form">
            {error && (
              <div className="error-box">
                <AlertCircle size={18} />
                <span>{error}</span>
              </div>
            )}

            <div className="form-card">
              <label>API Key</label>
              <p className="field-hint">
                Get your key from <a href="/integrations">Integrations</a>
              </p>
              <div className="api-key-input">
                <input
                  type="password"
                  placeholder="gc_live_xxxxx..."
                  value={manualKey}
                  onChange={(e) => setManualKey(e.target.value)}
                />
              </div>
              <button
                className="btn-primary start-btn"
                onClick={() => startDemo('production')}
                disabled={loading || !manualKey}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="spin" />
                    Starting...
                  </>
                ) : (
                  <>
                    <Play size={20} />
                    Start Verification
                  </>
                )}
              </button>
            </div>

            {isAuthenticated && (
              <button
                className="btn-secondary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => setManualKeyMode(false)}
              >
                Use my account's API key instead
              </button>
            )}
          </div>
        </div>
        <style>{sdkDemoStyles}</style>
      </div>
    );
  }

  // Main view: Mode selector
  return (
    <div className="sdk-demo-page">
      <div className="demo-container" style={{ maxWidth: '640px' }}>
        <div className="demo-header">
          <div className="logo">
            <Shield size={32} />
            <span>{BRAND.name}</span>
          </div>
          <h1>Verify Yourself</h1>
          <p>Experience the exact flow your users will see.</p>
        </div>

        {error && (
          <div className="error-box" style={{ marginBottom: '24px' }}>
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="loading-state">
            <Loader2 size={48} className="spin" />
            <h2>Starting verification...</h2>
            <p>Setting up your {mode === 'sandbox' ? 'sandbox' : 'production'} session</p>
          </div>
        ) : (
          <>
            {/* Mode selector cards */}
            <div className="mode-selector">
              {/* Sandbox */}
              <div className="mode-card" onClick={() => handleModeSelect('sandbox')}>
                <div className="mode-icon sandbox">
                  <FlaskConical size={28} />
                </div>
                <h3>Sandbox</h3>
                <p>Mock results, instant feedback. Good for seeing the UI flow quickly.</p>
                <button className="btn-secondary mode-btn">
                  <FlaskConical size={16} />
                  Try Sandbox
                </button>
              </div>

              {/* Production */}
              <div className="mode-card recommended" onClick={() => handleModeSelect('production')}>
                <div className="recommended-badge">Recommended</div>
                <div className="mode-icon production">
                  <Rocket size={28} />
                </div>
                <h3>Production</h3>
                <p>Real AI verification with actual risk scoring. Uses 1 of 1,000 free verifications.</p>
                <button className="btn-primary mode-btn">
                  <Rocket size={16} />
                  Go Live
                </button>
              </div>
            </div>

            {/* Pre-fill info */}
            {isAuthenticated && (
              <div className="prefill-notice">
                <CheckCircle size={14} />
                <span>
                  Pre-filled with your info: {firstName} {lastName} ({userEmail})
                </span>
              </div>
            )}
          </>
        )}
      </div>
      <style>{sdkDemoStyles}</style>
    </div>
  );
}

const sdkDemoStyles = `
  .sdk-demo-page {
    --primary: #0D9488;
    --accent: var(--accent-secondary);
    --primary-rgb: 13, 148, 136;
    min-height: 100vh;
    background: linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%);
    padding: 60px 20px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }

  .demo-container {
    max-width: 480px;
    margin: 0 auto;
  }

  /* Loading State */
  .loading-state {
    text-align: center;
    padding: 80px 20px;
  }

  .loading-state h2 {
    margin: 24px 0 8px;
    font-size: 24px;
    color: #111827;
  }

  .loading-state p {
    color: #6b7280;
  }

  .spin {
    animation: spin 1s linear infinite;
    color: var(--primary);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* Header */
  .demo-header {
    text-align: center;
    margin-bottom: 40px;
  }

  .logo {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: var(--primary);
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 20px;
  }

  .demo-header h1 {
    font-size: 32px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px;
  }

  .demo-header p {
    font-size: 16px;
    color: #6b7280;
  }

  /* Mode Selector */
  .mode-selector {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }

  .mode-card {
    background: white;
    border: 2px solid #e5e7eb;
    border-radius: 16px;
    padding: 28px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .mode-card:hover {
    border-color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  }

  .mode-card.recommended {
    border-color: var(--primary);
    background: linear-gradient(180deg, #f0fdfa 0%, #ffffff 40%);
  }

  .recommended-badge {
    position: absolute;
    top: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: var(--primary);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 12px;
    border-radius: 20px;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .mode-icon {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }

  .mode-icon.sandbox {
    background: #fef3c7;
    color: #d97706;
  }

  .mode-icon.production {
    background: #ccfbf1;
    color: var(--primary);
  }

  .mode-card h3 {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 8px;
  }

  .mode-card p {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.5;
    margin: 0 0 20px;
    flex: 1;
  }

  .mode-btn {
    width: 100%;
    justify-content: center;
  }

  .prefill-notice {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    color: #10b981;
  }

  /* Start Form (manual key fallback) */
  .start-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .form-card {
    background: white;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  }

  .form-card label {
    display: block;
    font-size: 15px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
  }

  .field-hint {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 12px;
  }

  .field-hint a {
    color: var(--primary);
    text-decoration: none;
  }

  .field-hint a:hover {
    text-decoration: underline;
  }

  .form-card input {
    width: 100%;
    padding: 14px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 15px;
    transition: border-color 0.2s;
    margin-bottom: 20px;
    box-sizing: border-box;
  }

  .form-card input:focus {
    outline: none;
    border-color: var(--primary);
  }

  .api-key-input {
    position: relative;
    margin-bottom: 20px;
  }

  .api-key-input input {
    margin-bottom: 0;
    padding-right: 44px;
  }

  .start-btn {
    width: 100%;
    justify-content: center;
  }

  /* Error Box */
  .error-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 12px;
    color: #dc2626;
    font-size: 14px;
  }

  /* Buttons */
  .btn-primary {
    padding: 14px 24px;
    background: linear-gradient(135deg, var(--primary), var(--accent));
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(var(--primary-rgb), 0.4);
  }

  .btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-secondary {
    padding: 14px 24px;
    background: white;
    color: #374151;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }

  .btn-secondary:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }

  /* SDK Fullscreen */
  .sdk-fullscreen {
    position: relative;
  }

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    cursor: pointer;
    margin-bottom: 20px;
    transition: all 0.2s;
  }

  .back-btn:hover {
    background: #f9fafb;
    color: #374151;
  }

  /* Result State */
  .result-state {
    text-align: center;
    background: white;
    border-radius: 16px;
    padding: 48px 32px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  }

  .result-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
  }

  .result-icon.success {
    background: #dcfce7;
    color: #16a34a;
  }

  .result-state h2 {
    font-size: 24px;
    color: #111827;
    margin: 0 0 8px;
  }

  .result-state p {
    color: #6b7280;
    margin: 0 0 28px;
  }

  .result-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  @media (max-width: 640px) {
    .sdk-demo-page {
      padding: 40px 16px;
    }

    .demo-header h1 {
      font-size: 26px;
    }

    .mode-selector {
      grid-template-columns: 1fr;
    }

    .form-card {
      padding: 24px;
    }

    .result-actions {
      flex-direction: column;
    }
  }
`;
